import { message } from "antd";
import { DeliverablesFilterParams } from "../pages/DeliverableList/DeliverableList.types";
import { TemplateJsonResponse } from "../../../shared/screen-components/AgGridComponent/AgGridUtils";
import { ConditionalFormattingRule } from "../../../shared/screen-components/ExcelViewComponent/ExcelViewComponent.types";
import { FilterKeyTypes } from "../../../shared/types/FilterKeys.types";
import { SheetSort } from "../../../shared/screen-components/AgGridComponent/hooks/sheetSort";
import { ENV } from "../../../config";
import {
  DEFAULT_FILTER_KEYS,
  GLOBAL_CONSTANT,
} from "../../../shared/utils/CommonConstants";
import ApiService from "../../../core/services/ApiService";

const CLIENT_ID = ENV.CLIENT_ID;
export interface CreateDeliverablePayload {
  name: string;
  ruleEngineId: number;
  coaId?: number | null;
  tag?: { id: number; value: string }[];
}
class DeliverableService extends ApiService {
  basePath: string = "/api";

  async getAllFeatues<T>(userId: string, organizationId: string): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/user/feature?user-id=${userId}&organization-id=${organizationId}`,
      {
        method: "get",
        extractData: true,
      },
    );
  }

  /**
   * The function `getAllDeliverables` asynchronously retrieves a list of deliverables with optional
   * pagination parameters.
   */
  async getAllDeliverables<T>(
    filterParams?: DeliverablesFilterParams | {},
  ): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/v2/deliverable?client-id=${CLIENT_ID}`,
      {
        method: "post",
        data: filterParams || ({} as any),
        extractData: true,
      },
    );
  }

  /**
   * This function asynchronously filters deliverables
   */
  async deliverablesFilter<T>(): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/filter-options?client-id=${CLIENT_ID}`,
      {
        method: "get",
        extractData: true,
      },
    );
  }

  /**
  * The function `getDeliverables` in TypeScript makes a POST request to retrieve deliverables based on
  * filter parameters.
 
  */
  async getDeliverables<T>(filterParams: DeliverablesFilterParams): Promise<T> {
    const url = `${this.basePath}/deliverable`;
    const response = await this.makeRequest<T>(url, {
      method: "POST",
      data: filterParams as any,
      extractData: true,
    });
    return response;
  }

  // post request to create deliverable using CreateDeliverablePayload as params
  async createDeliverable<T>(
    projectDetails: CreateDeliverablePayload,
  ): Promise<T> {
    return this.makeRequest<T>(`${this.basePath}/deliverable/create`, {
      method: "post",
      data: projectDetails as any,
      extractData: true,
    });
  }
  async getRefreshToken<T>(refreshToken: string): Promise<T> {
    const url = `${this.basePath}/auth/refresh-token`;
    const response = await this.makeRequest<T>(url, {
      method: "POST",
      data: { refreshToken } as any,
      extractData: true,
    });
    return response;
  }
  async getMultiSheetDetails<T>(id: number, sheetId: number) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/${sheetId}/multi-sheet-list`,
      {
        method: "get",
      },
    );
  }

  /**
 * The function `getDeliverableMenuDetails` makes an asynchronous request to retrieve menu hierarchy
 * details for a specific deliverable ID.
.
 */
  async getDeliverableMenuDetails<T>(id: number) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/menu-hierarchy`,
      {
        method: "get",
        extractData: true,
      },
    );
  }
  /**
   * The function `getDeliverableDetailsById` asynchronously retrieves deliverable details  using a
   * deliverable ID.
   */
  async getDeliverableDetailsById<T>(id: number) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "get",
      extractData: true,
    });
  }

  /**
   * The function `updateDeliverableName` asynchronously updates the name of a deliverable based on deliverable ID.
   */
  async updateDeliverableName<T>(id: number, name: { name: string }) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "put",
      data: name as any,
    });
  }

  /**
   * This TypeScript function retrieves project sheet data based on specified parameters such as
   * deliverable ID, sheet ID, start index, row size, page number, and filtered keys.
   */
  async getProjectSheetsData<T>(
    deliverableId: number,
    sheetId: number,
    startIndex = 0,
    rowSize = 50,
    pageNumber?: string,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
  ) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}?start-index=${
        startIndex > 0 ? startIndex : 0
      }&row-size=${rowSize}&sheet-number=${pageNumber ?? ""}&key1=${
        filteredKeys.Key1
      }&key2=${filteredKeys.Key2}&key3=${filteredKeys.Key3}&key4=${
        filteredKeys.Key4
      }`,
      { method: "get" },
    );
  }

  async getDeliverableTemplate<T>(id: number) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}/template`, {
      method: "get",
    });
  }

  /**
   * Scroll loading: one block of a deliverable sheet's grid JSON. A Parquet-backed sheet returns
   * rowSize rows from 0-based data row startIndex (fromParquet in the response, each row with
   * __rowIndex); any other sheet is returned whole (it is saved by full import). With a sort the BE
   * orders the whole sheet by that grid field before the block is cut; rows keep their __rowIndex.
   */
  async getSheetBlock(
    deliverableId: number,
    sheetId: number,
    startIndex: number,
    rowSize: number,
    sort?: SheetSort | null,
  ): Promise<TemplateJsonResponse | undefined> {
    return this.makeRequest<TemplateJsonResponse>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
      {
        params: {
          "is-json": true,
          paged: true,
          "start-index": startIndex,
          "row-size": rowSize,
          // Server-side sort of the whole sheet before the block is cut (TC-19.5.12).
          ...(sort ? { "sort-by": sort.field, "sort-direction": sort.direction } : {}),
        },
        extractData: true,
      },
    );
  }

  /**
   * Row save API (POST /deliverable/{id}/{sheetId}/rows): the loaded rows by their data row index
   * (null = new row, appended) and the deleted rows' indexes, under the edit session's locks.
   * Rows that were not loaded keep their values.
   */
  async saveSheetRows(
    deliverableId: string,
    sheetId: string,
    payload: { rows: { rowIndex: number | null; values: Record<string, unknown> }[]; deletedRowIndexes?: number[] },
    editSessionId?: string,
  ): Promise<void> {
    await this.makeRequest(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/rows`,
      {
        method: "post",
        data: payload,
        params: editSessionId ? { "edit-session-id": editSessionId } : undefined,
        extractData: true,
      },
    );
  }

  //** Get Excel json response* */
  async getTemplateUsingAxiosWithJson(
    deliverableId: number,
    sheetId: number,
    multiSheet?: boolean,
    startIndex: number = 0,
    rowSize: number = 10,
    filteredKeys?: FilterKeyTypes,
    isJson: boolean = false,
  ): Promise<TemplateJsonResponse | undefined> {
    try {
      return this.makeRequest<TemplateJsonResponse>(
        `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
        {
          params: { "is-json": isJson },
          extractData: true,
        },
      );
    } catch (error) {
      message.error(GLOBAL_CONSTANT.TOAST_DEFAULT_ERROR_MESSAGE);
    }
  }
  /**
   * This TypeScript function uses Axios to fetch a deliverable template file for a specific deliverable and sheet,
   * including necessary headers for authentication and access control.
   */

  async getTemplateUsingAxios(
    deliverableId: number,
    sheetId: number,
    multiSheet?: boolean,
    startIndex: number = 0,
    rowSize: number = 10,
    filteredKeys?: FilterKeyTypes,
    isJson: boolean = false,
  ): Promise<Blob | undefined> {
    try {
      return this.makeRequest<Blob>(
        `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
        {
          responseType: "blob",
          params: { "is-json": isJson },
          extractData: true,
        },
      );
    } catch (error) {
      message.error(GLOBAL_CONSTANT.TOAST_DEFAULT_ERROR_MESSAGE);
    }
  }

  async getDeliverableTemplateUsingAxios(
    deliverableId: number,
    isJson: boolean = false,
  ): Promise<Blob> {
    return this.makeRequest<Blob>(
      `${this.basePath}/deliverable/${deliverableId}/template`,
      {
        responseType: "blob",
        params: { "is-json": isJson },
        extractData: true,
      },
    );
  }

  /**
   * The function `getConditionalFormattingUsingAxios` makes an asynchronous request to retrieve
   * conditional formatting rules for deliverable template using Axios with specified parameters.
   */
  async getConditionalFormattingUsingAxios(
    deliverableId: number,
    sheetId: number,
  ): Promise<ConditionalFormattingRule[]> {
    return this.makeRequest<ConditionalFormattingRule[]>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template-conditional-format`,
      { extractData: true },
    );
  }
  /**
   * The function `import` in TypeScript asynchronously imports a file using FormData and makes a POST
   * request to a specified endpoint with optional start index.
   */
  async import<T>(
    id: string,
    sheetId: string,
    file: File,
    multiSheet?: boolean,
    startIndex: number = 0,
    isAgGridImport: boolean = false,
    editSessionId?: string,
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file", file);
    // TC-19.2.12: Save from Edit sends its edit session so the import reuses the session's locks.
    const editSession = editSessionId
      ? `&edit-session-id=${encodeURIComponent(editSessionId)}`
      : "";
    return this.makeRequest(
      `${this.basePath}/deliverable/${id}/${sheetId}/import?start-index=${
        startIndex < 1 ? 0 : startIndex
      }&ag-grid-import=${isAgGridImport}${editSession}`,
      {
        method: "post",
        data: formData,
        extractData: true,
      },
    );
  }

  /**
   * This TypeScript function uses Axios to make an asynchronous GET request to export data from a
   * specific sheet with optional filtering parameters.
   */
  async exportAxios<T>(
    id: string,
    sheetId: string,
    multiSheet?: boolean,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
    rowCount?: number,
    sheetNumber?: string,
  ): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/${sheetId}/export`,
      {
        responseType: "blob",
        params: {
          "multi-sheet": multiSheet ?? false,
          "keys.Key1": filteredKeys.Key1,
          "keys.Key2": filteredKeys.Key2,
          "keys.Key3": filteredKeys.Key3,
          "keys.Key4": filteredKeys.Key4,
          "add-default-row": rowCount,
        },
        extractData: true,
      },
    );
  }

  /**
   * This TypeScript function updates project details by making a PUT request to a specific endpoint with
   * the provided data.
   */
  async updateProjectDetails<T>(id: number, data: any) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "put",
      data: data,
    });
  }

  /**
 * This TypeScript function asynchronously retrieves a key sequence for a specific deliverable and
 * sheet.

 */
  async getKeySequence<T>(deliverableId: any, sheetId: any) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/key-sequence`,
    );
  }
  /**
   * This TypeScript function retrieves the status of a task associated with a deliverable using an
   * asynchronous HTTP GET request.
   */
  async taskStatus<T>(
    deliverableId: any,
    sheetId?: string,
    taskid?: number,
    taskType?: string,
  ): Promise<T | null> {
    try {
      return this.makeRequest<T>(
        `${this.basePath}/deliverable/${deliverableId}/task-status`,
        {
          params: {
            "sheet-id": sheetId ?? "",
            "task-id": taskid ?? "",
            "task-type": taskType ?? "",
          },
          extractData: true,
        },
      );
    } catch {
      return null;
    }
  }

  // ---- TC-19.2.12: sheet edit session locks ----

  /** Whether the sheet is locked by an edit session or import, and by whom. */
  async getSheetEditLock(
    deliverableId: string,
    sheetId: string,
  ): Promise<SheetLockStatus> {
    return this.makeRequest<SheetLockStatus>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/edit-lock`,
      { method: "get", extractData: true },
    );
  }

  /** Starts an edit session (Edit button); rejects with a 409 body when the sheet set is locked. */
  async acquireSheetEditLock(
    deliverableId: string,
    sheetId: string,
  ): Promise<SheetEditLock> {
    return this.makeRequest<SheetEditLock>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/edit-lock`,
      { method: "post", extractData: true },
    );
  }

  /** Renews the edit session's locks (heartbeat while editing). */
  async renewSheetEditLock(sessionId: string): Promise<SheetEditLock> {
    return this.makeRequest<SheetEditLock>(
      `${this.basePath}/deliverable/edit-lock/${encodeURIComponent(sessionId)}`,
      { method: "put", extractData: true },
    );
  }

  /** Ends the edit session without saving (Cancel button). */
  async releaseSheetEditLock(sessionId: string): Promise<void> {
    await this.makeRequest(
      `${this.basePath}/deliverable/edit-lock/${encodeURIComponent(sessionId)}`,
      { method: "delete" },
    );
  }
}

/** GET /api/deliverable/{id}/{sheet-id}/edit-lock (TC-19.2.12). */
export interface SheetLockStatus {
  isLocked: boolean;
  lockType: "edit" | "import" | null;
  lockedByUserId: string | null;
  lockedByName: string | null;
  lockedByCurrentUser: boolean;
  sessionId: string | null;
  expiresAt: string | null;
}

/** POST/PUT edit-lock response (TC-19.2.12). */
export interface SheetEditLock {
  sessionId: string;
  expiresAt: string;
  heartbeatIntervalSeconds: number;
  lockedSheetCount: number;
}

export default DeliverableService;
